import React from "react";
import { Breadcrumb, Button, Layout, theme, Select } from "antd";
import { MenuOutlined, DownOutlined } from "@ant-design/icons";
import { useLocation, useNavigate } from "react-router-dom";
import { usePractice } from "../Context/PracticeContext";
import { BREAKPOINT_SIDEBAR, HEADER_HEIGHT } from "./Layout/constants";

const { Header } = Layout;

interface AppHeaderProps {
  screenWidth: number;
  onMenuOpen: () => void;
}

const ROUTE_LABELS: Record<string, string> = {
  updates: "Releases",
  holidays: "Bank Holidays",
  "practice-data-requests": "Practice Onboarding",
  ctconfiguration: "CT Configuration",
  payrollyearlysettings: "Payroll Yearly Settings",
  submissionhold: "Submission Hold",
  generalsettingbooks: "Books General Settings",
  adminusers: "Users",
  adminuser: "Profile",
  "fyi-import": "FYI Import",
  "ticket-alert-templates": "Ticket alert templates",
  sa100configuration: "SA100 Configuration",
};

const formatSegment = (
  str: string,
  allPracticeIds?: { value: number; label: string }[],
  dynamicPathLabels?: Record<string, string>,
): string => {
  if (dynamicPathLabels && dynamicPathLabels[str]) {
    return dynamicPathLabels[str];
  }
  const numeric = parseInt(str, 10);
  if (!isNaN(numeric) && allPracticeIds) {
    const match = allPracticeIds.find((p) => p.value === numeric);
    if (match) return match.label;
  }
  const lower = str.toLowerCase();
  if (ROUTE_LABELS[lower]) return ROUTE_LABELS[lower];
  return str
    .replace(/[-_]/g, " ")
    .split(/(?=[A-Z])| /)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");
};

const AppHeader: React.FC<AppHeaderProps> = ({ screenWidth, onMenuOpen }) => {
  const { token } = theme.useToken();
  const location = useLocation();
  const navigate = useNavigate();
  const { allPracticeIds } = usePractice();

  const [labelTrigger, setLabelTrigger] = React.useState(0);
  React.useEffect(() => {
    const handler = () => setLabelTrigger((prev) => prev + 1);
    window.addEventListener("dynamicPathLabelsUpdate", handler);
    return () => window.removeEventListener("dynamicPathLabelsUpdate", handler);
  }, []);

  const segments = location.pathname.split("/").filter(Boolean);
  const dynamicLabels = (window as any).dynamicPathLabels || {};

  return (
    <Header
      className="site-layout-background aoa-layout"
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        height: HEADER_HEIGHT,
        padding: "0 16px",
        background: token.colorBgContainer,
        borderBottom: `1px solid ${token.colorBorderSecondary}`,
        position: "sticky",
        top: 0,
        zIndex: 1,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        {screenWidth < BREAKPOINT_SIDEBAR && (
          <Button
            type="text"
            icon={
              <MenuOutlined
                style={{ fontSize: 16, color: token.colorTextSecondary }}
              />
            }
            onClick={onMenuOpen}
            style={{
              padding: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 28,
              height: 28,
            }}
          />
        )}

        <Breadcrumb
          separator={
            <span style={{ color: token.colorBorderSecondary }}>/</span>
          }
          style={{ fontSize: 12 }}
        >
          <Breadcrumb.Item key="home">
            <span
              style={{
                color: token.colorTextSecondary,
                fontWeight: 500,
                cursor: "pointer",
              }}
              onClick={() => navigate("/")}
            >
              Home
            </span>
          </Breadcrumb.Item>

          {segments.map((seg, index) => {
            const isLast = index === segments.length - 1;
            const to = `/${segments.slice(0, index + 1).join("/")}`;
            const label = formatSegment(seg, allPracticeIds, dynamicLabels);
            const isNumericPractice =
              isLast &&
              !isNaN(parseInt(seg, 10)) &&
              segments[index - 1]?.toLowerCase() === "practices";

            if (
              isNumericPractice &&
              allPracticeIds &&
              allPracticeIds.length > 0
            ) {
              const currentId = parseInt(seg, 10);
              return (
                <Breadcrumb.Item key={to}>
                  <Select
                    variant="borderless"
                    bordered={false}
                    showSearch
                    value={currentId}
                    style={{
                      fontSize: 12,
                      fontWeight: 600,
                      color: token.colorText,
                      margin: "-4px 0",
                      padding: 0,
                      minWidth: 100,
                    }}
                    onChange={(selectedId) => {
                      navigate(`/practices/${selectedId}`);
                    }}
                    options={allPracticeIds}
                    filterOption={(input, option) =>
                      (option?.label?.toString() ?? "")
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                    suffixIcon={
                      <DownOutlined
                        style={{ fontSize: 9, color: token.colorTextSecondary }}
                      />
                    }
                  />
                </Breadcrumb.Item>
              );
            }

            return isLast ? (
              <Breadcrumb.Item key={to}>
                <span style={{ color: token.colorText, fontWeight: 600 }}>
                  {label}
                </span>
              </Breadcrumb.Item>
            ) : (
              <Breadcrumb.Item key={to}>
                <span
                  style={{
                    color: token.colorTextSecondary,
                    fontWeight: 500,
                    cursor: "pointer",
                  }}
                  onClick={() => navigate(to)}
                >
                  {label}
                </span>
              </Breadcrumb.Item>
            );
          })}
        </Breadcrumb>
      </div>
    </Header>
  );
};

export default AppHeader;
